import { Portfolio } from "@/types/Portfolio";

export type NewsquawkCategory =
  | "Indices"
  | "Technology"
  | "Energy"
  | "Financials"
  | "Industrials"
  | "Healthcare"
  | "Consumer"
  | "Materials"
  | "Utilities"
  | "Real Estate"
  | "Communications"
  | "Macro"
  | "Other";

export interface CategoryConfig {
  id: NewsquawkCategory;
  label: string;
  description: string;
  defaultCollapsed?: boolean;
}

export const NEWSQUAWK_CATEGORIES: CategoryConfig[] = [
  {
    id: "Indices",
    label: "Indices",
    description: "Major market indices",
    defaultCollapsed: false,
  },
  {
    id: "Technology",
    label: "Technology",
    description: "Technology sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Energy",
    label: "Energy",
    description: "Energy sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Financials",
    label: "Financials",
    description: "Financial sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Industrials",
    label: "Industrials",
    description: "Industrial sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Healthcare",
    label: "Healthcare",
    description: "Healthcare sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Consumer",
    label: "Consumer",
    description: "Consumer sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Materials",
    label: "Materials",
    description: "Materials sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Utilities",
    label: "Utilities",
    description: "Utilities sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Real Estate",
    label: "Real Estate",
    description: "Real estate sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Communications",
    label: "Communications",
    description: "Communications sector watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Macro",
    label: "Macro",
    description: "Economic and macro watchlists",
    defaultCollapsed: false,
  },
  {
    id: "Other",
    label: "Other",
    description: "General watchlists",
    defaultCollapsed: true,
  },
];

// Pattern-based category inference from watchlist names
// These patterns will be matched case-insensitively against watchlist names
const CATEGORY_PATTERNS: { category: NewsquawkCategory; patterns: RegExp[] }[] = [
  {
    category: "Indices",
    patterns: [
      /s&p\s*500/i,
      /spx/i,
      /spy/i,
      /nasdaq/i,
      /qqq/i,
      /dow\s*jones/i,
      /djia/i,
      /dia/i,
      /russell/i,
      /iwm/i,
      /nyse/i,
      /index/i,
      /indices/i,
      /composite/i,
      /ftse/i,
      /dax/i,
      /cac/i,
      /nikkei/i,
      /hang\s*seng/i,
      /stoxx/i,
    ],
  },
  {
    category: "Technology",
    patterns: [
      /tech(nology)?/i,
      /semiconductor/i,
      /software/i,
      /xlk/i,
      /qqq/i,
      /smh/i,
    ],
  },
  {
    category: "Energy",
    patterns: [
      /energy/i,
      /oil/i,
      /gas\b/i,
      /xle/i,
      /xop/i,
      /uso/i,
    ],
  },
  {
    category: "Financials",
    patterns: [
      /financ(e|ial)/i,
      /banking/i,
      /bank\b/i,
      /insurance/i,
      /xlf/i,
      /kbe/i,
      /kre/i,
    ],
  },
  {
    category: "Industrials",
    patterns: [
      /industrial/i,
      /transport/i,
      /airline/i,
      /aerospace/i,
      /defense/i,
      /xli/i,
      /iyt/i,
    ],
  },
  {
    category: "Healthcare",
    patterns: [
      /healthcare/i,
      /health\s*care/i,
      /pharma/i,
      /bio(tech)?/i,
      /medical/i,
      /xlv/i,
      /xbi/i,
      /ibb/i,
    ],
  },
  {
    category: "Consumer",
    patterns: [
      /consumer/i,
      /retail/i,
      /discretionary/i,
      /staples/i,
      /xly/i,
      /xlp/i,
      /xrt/i,
    ],
  },
  {
    category: "Materials",
    patterns: [
      /material/i,
      /mining/i,
      /metal/i,
      /steel/i,
      /gold/i,
      /silver/i,
      /copper/i,
      /commodit/i,
      /xlb/i,
      /gdx/i,
      /gld/i,
    ],
  },
  {
    category: "Utilities",
    patterns: [
      /utilit(y|ies)/i,
      /xlu/i,
    ],
  },
  {
    category: "Real Estate",
    patterns: [
      /real\s*estate/i,
      /reit/i,
      /xlre/i,
      /vnq/i,
    ],
  },
  {
    category: "Communications",
    patterns: [
      /communication/i,
      /telecom/i,
      /media/i,
      /entertainment/i,
      /xlc/i,
    ],
  },
  {
    category: "Macro",
    patterns: [
      /fed(eral)?/i,
      /fomc/i,
      /\brate/i,
      /inflation/i,
      /gdp/i,
      /cpi/i,
      /ppi/i,
      /employment/i,
      /jobs/i,
      /nfp/i,
      /payroll/i,
      /central\s*bank/i,
      /ecb/i,
      /boe/i,
      /boj/i,
      /monetary/i,
      /fiscal/i,
      /treasur/i,
      /bond/i,
      /yield/i,
      /debt/i,
      /deficit/i,
      /stimulus/i,
      /quantitative/i,
      /hawkish/i,
      /dovish/i,
      /recession/i,
      /economic/i,
      /macro/i,
    ],
  },
];

/**
 * Infer the category of a Newsquawk watchlist based on its name.
 * Uses pattern matching to categorize watchlists.
 * Returns "Other" if no patterns match.
 */
export function categorizeWatchlist(portfolio: Portfolio): NewsquawkCategory {
  const name = portfolio.name;

  for (const { category, patterns } of CATEGORY_PATTERNS) {
    for (const pattern of patterns) {
      if (pattern.test(name)) {
        return category;
      }
    }
  }

  return "Other";
}

/**
 * Group an array of watchlists by their inferred category.
 * Returns an object with category keys and arrays of watchlists.
 */
export function groupWatchlistsByCategory(
  watchlists: Portfolio[]
): Record<NewsquawkCategory, Portfolio[]> {
  const grouped: Record<NewsquawkCategory, Portfolio[]> = {
    Indices: [],
    Technology: [],
    Energy: [],
    Financials: [],
    Industrials: [],
    Healthcare: [],
    Consumer: [],
    Materials: [],
    Utilities: [],
    "Real Estate": [],
    Communications: [],
    Macro: [],
    Other: [],
  };

  for (const watchlist of watchlists) {
    const category = categorizeWatchlist(watchlist);
    grouped[category].push(watchlist);
  }

  // Sort each category by priority
  for (const category of Object.keys(grouped) as NewsquawkCategory[]) {
    grouped[category].sort((a, b) => a.priority - b.priority);
  }

  return grouped;
}

/**
 * Get the count of watchlists in each category.
 */
export function getCategoryCounts(
  watchlists: Portfolio[]
): Record<NewsquawkCategory, number> {
  const grouped = groupWatchlistsByCategory(watchlists);
  return {
    Indices: grouped.Indices.length,
    Technology: grouped.Technology.length,
    Energy: grouped.Energy.length,
    Financials: grouped.Financials.length,
    Industrials: grouped.Industrials.length,
    Healthcare: grouped.Healthcare.length,
    Consumer: grouped.Consumer.length,
    Materials: grouped.Materials.length,
    Utilities: grouped.Utilities.length,
    "Real Estate": grouped["Real Estate"].length,
    Communications: grouped.Communications.length,
    Macro: grouped.Macro.length,
    Other: grouped.Other.length,
  };
}

/**
 * Get categories that have at least one watchlist.
 */
export function getNonEmptyCategories(
  watchlists: Portfolio[]
): CategoryConfig[] {
  const counts = getCategoryCounts(watchlists);
  return NEWSQUAWK_CATEGORIES.filter((cat) => counts[cat.id] > 0);
}
